import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatLabel } from "@/lib/api";
import { ArrowLeft, ClipboardList, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const ACTION_LABELS = {
  create: "Created",
  update: "Updated",
  publish: "Published",
  unpublish: "Unpublished",
  delete: "Deleted",
  state_change: "State change",
  citation_verify: "Citation verified",
  citation_unverify: "Citation unverified",
  admin_added: "Admin added",
  admin_removed: "Admin removed",
  tip_status_change: "Tip status changed",
  subscriber_deleted: "Subscriber deleted",
  ingest_draft_created: "Ingest draft",
  agent_publish_undone: "Agent publish undone",
  agent_settings_changed: "Agent settings changed",
};

const ACTION_COLORS = {
  create: "#008A00",
  publish: "#008A00",
  delete: "#D32F2F",
  unpublish: "#D32F2F",
  agent_publish_undone: "#D32F2F",
  update: "#0050FF",
  ingest_draft_created: "#8B5CF6",
};

export default function AdminAuditLog() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (actionFilter) params.action = actionFilter;
      const r = await api.get("/admin/audit-log", { params });
      setData(r.data);
    } catch {
      toast.error("Failed to load audit log");
    } finally { setLoading(false); }
  }, [actionFilter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <Link to="/admin" className="inline-flex items-center gap-2 font-mono-plex text-sm uppercase tracking-wider hover:underline" data-testid="back-to-admin">
        <ArrowLeft className="w-4 h-4" /> Back to admin
      </Link>

      <div className="mt-4 border-b border-black/15 pb-6">
        <div className="overline">/transparency</div>
        <h1 className="font-display text-4xl font-black tracking-tighter mt-2">Audit log</h1>
        <p className="text-base text-[#333] mt-3 max-w-2xl">
          Every editorial action — create, update, publish, unpublish, delete, citation-verify,
          and automated daily-ingest drafts — is recorded here with the actor, the target, and a
          field-level diff. Tamper-evident transparency for the public record.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-6">
        <span className="font-mono-plex text-xs uppercase tracking-wider text-[#555]">Filter:</span>
        {["", "create", "update", "publish", "unpublish", "agent_publish_undone", "agent_settings_changed", "delete", "ingest_draft_created"].map((a) => (
          <button
            key={a || "all"}
            data-testid={`filter-${a || "all"}`}
            onClick={() => setActionFilter(a)}
            className={`px-3 py-1.5 font-mono-plex text-xs uppercase tracking-wider border ${
              actionFilter === a ? "bg-black text-white border-black" : "bg-white border-black/30 hover:border-black"
            }`}
          >
            {a ? ACTION_LABELS[a] || a : "All"}
          </button>
        ))}
        <Button onClick={load} variant="outline" className="rounded-none border-black h-9 ml-auto" data-testid="refresh-log">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      <div className="mt-6 border border-black/15 bg-white">
        {loading && <div className="p-6 font-mono-plex text-sm text-[#555] uppercase tracking-wider">Loading…</div>}
        {!loading && data && data.entries.length === 0 && (
          <div className="p-8 text-center text-[#555]">
            <ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-40" />
            No entries match this filter yet.
          </div>
        )}
        {!loading && data && data.entries.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="font-mono-plex uppercase text-[11px] tracking-[0.2em] text-[#555] bg-[#FAFAFA]">
                <th className="text-left px-4 py-3">When (UTC)</th>
                <th className="text-left px-4 py-3">Actor</th>
                <th className="text-left px-4 py-3">Action</th>
                <th className="text-left px-4 py-3">Target</th>
                <th className="text-left px-4 py-3">Changes</th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map((e) => (
                <tr key={e.id} className="border-t border-black/10" data-testid={`audit-row-${e.id}`}>
                  <td className="px-4 py-3 font-mono-plex text-xs text-[#555] whitespace-nowrap">{e.at?.slice(0, 19).replace("T", " ")}</td>
                  <td className="px-4 py-3 text-[#333]">{e.actor}</td>
                  <td className="px-4 py-3">
                    <span
                      className="inline-block px-2 py-0.5 font-mono-plex text-xs uppercase tracking-wider text-white"
                      style={{ background: ACTION_COLORS[e.action] || "#555" }}
                    >
                      {ACTION_LABELS[e.action] || e.action}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {e.target_id ? (
                      <Link to={`/entry/${e.target_id}`} target="_blank" rel="noreferrer" className="hover:underline">
                        {e.target_title || e.target_id.slice(0, 8)}
                      </Link>
                    ) : (
                      <span className="text-[#555]">{e.target_title || e.target_type}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#555]">
                    {e.note && <div className="mb-1 text-[#333]">{e.note}</div>}
                    <DiffView diff={e.diff} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function DiffView({ diff }) {
  if (!diff || Object.keys(diff).length === 0) return <span className="text-[#888]">—</span>;
  if (diff._created) return <span className="text-[#008A00]">new record</span>;
  if (diff._deleted) return <span className="text-[#D32F2F]">deleted</span>;
  const keys = Object.keys(diff).slice(0, 5);
  return (
    <div className="space-y-1">
      {keys.map((k) => (
        <div key={k} className="font-mono-plex text-[11px]">
          <span className="text-[#333] font-bold">{k}:</span>{" "}
          <span className="text-[#D32F2F] line-through">{stringify(diff[k][0])}</span>{" → "}
          <span className="text-[#008A00]">{stringify(diff[k][1])}</span>
        </div>
      ))}
      {Object.keys(diff).length > 5 && <div className="text-[#888]">+{Object.keys(diff).length - 5} more…</div>}
    </div>
  );
}

function stringify(v) {
  if (v === null || v === undefined) return "—";
  if (Array.isArray(v)) return `[${v.length}]`;
  if (typeof v === "object") return "{…}";
  const s = String(v);
  return s.length > 60 ? s.slice(0, 60) + "…" : s;
}
